import { useCallback, useContext, useEffect, useState } from 'react'
import ConnectionContext from 'shared/contexts/ConnectionContext'

// Connection message configuration
type MessageLevel = 'connected' | 'warning' | 'critical'

type MessageConfig = {
  level: MessageLevel
  message: string
  icon: string
  autoDismiss?: number // ms, undefined = persistent
  priority: number // lower = higher priority
}

const CONNECTION_MESSAGES: Record<string, MessageConfig> = {
  net_down: {
    level: 'critical',
    message: 'No internet connection',
    icon: '⚠️',
    priority: 1
  },
  back_down: {
    level: 'critical',
    message: 'Reconnecting to server...',
    icon: '🔄',
    priority: 2
  },
  hg_down: {
    level: 'warning',
    message: 'Connection unstable',
    icon: '⚠️',
    priority: 3
  },
  dg_down: {
    level: 'warning',
    message: 'Audio may be limited',
    icon: '⚠️',
    autoDismiss: 5000,
    priority: 4
  },
  slow_network: {
    level: 'warning',
    message: 'Connection slow',
    icon: '⚠️',
    priority: 5
  },
  recovered: {
    level: 'connected',
    message: 'Connection restored',
    icon: '✓',
    autoDismiss: 3000,
    priority: 6
  }
}

type ConnectionState = Record<string, boolean>

const ConnectionStatus = () => {
  const { connectionStatus } = useContext(ConnectionContext)
  const [previousStatus, setPreviousStatus] = useState<ConnectionState | null>(null)
  const [showRecovered, setShowRecovered] = useState(false)
  const [currentMessage, setCurrentMessage] = useState<MessageConfig | null>(null)

  // Helper to check if a service was previously disconnected
  const wasPreviouslyDisconnected = useCallback((prev: ConnectionState | null): boolean => {
    if (!prev) return false
    return prev.Net === false || prev.back === false || prev.HG === false || prev.DG === false || prev.slowNet === false
  }, [])

  // Determine which message to show based on connection state
  const getConnectionMessage = useCallback((
    current: ConnectionState,
    previous: ConnectionState | null
  ): MessageConfig | null => {
    // Empty or incomplete state - don't show anything yet
    // Wait until we have info about critical services (Net, back)
    if (Object.keys(current).length === 0) {
      return null
    }

    // Check critical issues (highest priority)
    if (current.Net === false) return CONNECTION_MESSAGES.net_down
    if (current.back === false) return CONNECTION_MESSAGES.back_down

    // Check warnings
    if (current.HG === false) return CONNECTION_MESSAGES.hg_down
    if (current.DG === false) return CONNECTION_MESSAGES.dg_down
    if (current.slowNet === false) return CONNECTION_MESSAGES.slow_network

    // All known services are connected
    const allConnected =
      current.Net === true &&
      current.back === true &&
      current.HG === true &&
      current.DG === true &&
      current.slowNet === true

    // Show recovery message if we had a previous disconnection
    if (allConnected && wasPreviouslyDisconnected(previous)) {
      return CONNECTION_MESSAGES.recovered
    }

    // All good, nothing to show
    return null
  }, [wasPreviouslyDisconnected])

  useEffect(() => {
    const message = getConnectionMessage(connectionStatus, previousStatus)

    // Update current message
    setCurrentMessage(message)

    // Handle recovery message
    if (message?.level === 'connected' && message.autoDismiss) {
      setShowRecovered(true)
      const timer = setTimeout(() => {
        setShowRecovered(false)
        setCurrentMessage(null)
      }, message.autoDismiss)

      setPreviousStatus(connectionStatus)
      return () => clearTimeout(timer)
    }

    // Handle auto-dismiss warnings (DG)
    if (message?.autoDismiss && message.level === 'warning') {
      const timer = setTimeout(() => {
        setCurrentMessage(null)
      }, message.autoDismiss)

      setPreviousStatus(connectionStatus)
      return () => clearTimeout(timer)
    }

    // Update previous status for next comparison
    setPreviousStatus(connectionStatus)
  }, [connectionStatus, previousStatus, getConnectionMessage])

  // Don't render if no message to show
  if (!currentMessage) return null

  // Don't show recovered message if flag is false
  if (currentMessage.level === 'connected' && !showRecovered) return null

  // Determine color classes based on level
  let colorClasses = ''
  if (currentMessage.level === 'connected') {
    colorClasses = 'bg-green-500/90 text-white'
  } else if (currentMessage.level === 'critical') {
    colorClasses = 'bg-red-600/95 text-white'
  } else if (currentMessage.level === 'warning') {
    colorClasses = 'bg-amber-500/90 text-white'
  }

  return (
    <div
      className={`
        fixed top-5 left-1/2 transform -translate-x-1/2 z-50
        px-4 py-2 rounded-full shadow-lg backdrop-blur-sm
        flex items-center gap-2 text-sm font-medium
        animate-fade-in transition-all duration-300
        ${colorClasses}
      `}
      role="status"
      aria-live="polite"
    >
      <span className="text-base">{currentMessage.icon}</span>
      <span>{currentMessage.message}</span>
    </div>
  )
}

export default ConnectionStatus
